import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="multi-step-form"
export default class extends Controller {
  static targets = ["step", "nextButton", "submitButton", "validationMessage", "validationText"]
  static values = { currentStep: Number, totalSteps: Number }

  connect() {
    this.updateStepVisibility()
    this.updateRequiredFieldsForCurrentStep()
  }

  // Navigate to next step
  nextStep() {
    if (this.validateCurrentStep()) {
      this.submitStepManually('next')
    } else {
      this.showValidationMessage("Please complete all required fields before continuing.")
    }
  }

  // Navigate to previous step
  previousStep() {
    this.submitStepManually('previous')
  }

  // Method called by form fields
  fieldChanged(event) {
    const field = event.target
    if (this.isFieldValid(field)) {
      this.clearFieldHighlight(field)
    }
    this.hideValidationMessage()
  }

  // Submit step data manually
  submitStepManually(direction = 'next') {
    const mainForm = this.element.closest('form')
    const formData = new FormData(mainForm)
    
    const tempForm = document.createElement('form')
    tempForm.method = 'POST'
    tempForm.action = '/register/step'
    tempForm.style.display = 'none'
    
    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      tempForm.appendChild(csrfInput)
    }
    
    // Add step and direction
    const stepInput = document.createElement('input')
    stepInput.type = 'hidden'
    stepInput.name = 'step'
    stepInput.value = this.currentStepValue
    tempForm.appendChild(stepInput)
    
    const directionInput = document.createElement('input')
    directionInput.type = 'hidden'
    directionInput.name = 'direction'
    directionInput.value = direction
    tempForm.appendChild(directionInput)
    
    // Add all form data
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('user[')) {
        const hiddenInput = document.createElement('input')
        hiddenInput.type = 'hidden'
        hiddenInput.name = key
        hiddenInput.value = value
        tempForm.appendChild(hiddenInput)
      }
    }
    
    document.body.appendChild(tempForm)
    tempForm.submit()
  }

  updateStepVisibility() {
    this.stepTargets.forEach((step, index) => {
      const stepNumber = index + 1
      const isCurrentStep = stepNumber === this.currentStepValue
      
      if (isCurrentStep) {
        step.classList.remove('hidden')
        step.classList.add('block')
      } else {
        step.classList.remove('block')
        step.classList.add('hidden')
      }
    })
    
    this.updateRequiredFieldsForCurrentStep()
  }

  // Dynamically update required fields based on current step
  updateRequiredFieldsForCurrentStep() {
    // Remove required attribute from all form fields
    this.element.querySelectorAll('[data-step-required]').forEach(field => {
      field.removeAttribute('required')
    })

    // Add required attribute only to fields in the current step
    const currentStepElement = this.stepTargets[this.currentStepValue - 1]
    if (currentStepElement) {
      currentStepElement.querySelectorAll('[data-step-required]').forEach(field => {
        field.setAttribute('required', 'required')
      })
    }
  }

  validateCurrentStep() {
    const currentStepElement = this.stepTargets[this.currentStepValue - 1]
    if (!currentStepElement) return true

    const requiredFields = currentStepElement.querySelectorAll('[data-step-required]')
    let isValid = true
    let firstInvalidField = null

    requiredFields.forEach(field => {
      if (!this.isFieldValid(field)) {
        isValid = false
        if (!firstInvalidField) {
          firstInvalidField = field
        }
        this.highlightInvalidField(field)
      } else {
        this.clearFieldHighlight(field)
      }
    })

    // Special validation for user type selection (step 1)
    if (this.currentStepValue === 1) {
      const userTypeRadios = currentStepElement.querySelectorAll('input[name="user[user_type]"]')
      const isUserTypeSelected = Array.from(userTypeRadios).some(radio => radio.checked)
      
      if (!isUserTypeSelected) {
        isValid = false
        this.showValidationMessage("Please select an account type to continue.")
      }
    }

    if (!isValid && firstInvalidField) {
      firstInvalidField.focus()
    }

    if (isValid) {
      this.hideValidationMessage()
    }

    return isValid
  }

  isFieldValid(field) {
    if (field.type === 'email') {
      return field.value.trim() !== '' && this.isValidEmail(field.value)
    }
    
    if (field.type === 'password') {
      return field.value.length >= 6
    }

    if (field.type === 'checkbox') {
      return field.checked
    }

    return field.value.trim() !== ''
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  highlightInvalidField(field) {
    field.classList.add('border-red-500', 'ring-red-500')
    field.classList.remove('border-gray-300', 'focus:border-primary', 'focus:ring-primary')
  }

  clearFieldHighlight(field) {
    field.classList.remove('border-red-500', 'ring-red-500')
    field.classList.add('border-gray-300', 'focus:border-primary', 'focus:ring-primary')
  }

  showValidationMessage(message = null) {
    if (this.hasValidationMessageTarget) {
      if (message && this.hasValidationTextTarget) {
        this.validationTextTarget.textContent = message
      }
      this.validationMessageTarget.classList.remove('hidden')
    }
  }

  hideValidationMessage() {
    if (this.hasValidationMessageTarget) {
      this.validationMessageTarget.classList.add('hidden')
    }
  }
}